// src/lib/posthog/replay-policy.ts
//
// Pure routing rules for session replay and per-surface capture config. The
// pathname is passed in by the caller (`client.ts`) on initial load and on
// every client navigation; this module never reads `window.location` itself.

import type { CapturedNetworkRequest, PostHogConfig } from "posthog-js";

import { redactUrl } from "./sanitize";

// The fixed set of blocked paths. `/fiat` is dropped (no page exists there).
export const REPLAY_BLOCKED_PATH_PREFIXES = [
  "/buy",
  "/onramping",
  "/ramping",
  "/paylink",
  "/card",
  "/kyc",
  "/kyb",
] as const;

// Every top-level folder under `src/app/(dashboard)`.
// `replay-policy.spec.ts` reads the real folder list and fails closed the
// moment a new one is added here without updating this list.
export const DASHBOARD_SEGMENTS = [
  "banking",
  "blocked-devices",
  "card",
  "chargeback-cases",
  "chargebacks",
  "company-profile",
  "customers",
  "dashboard",
  "fraud-alerts",
  "help-requests",
  "invoices",
  "new-orders-view",
  "open-banking",
  "orders",
  "payouts",
  "settings",
  "stuck-orders",
  "wallets",
] as const;

// Non-marketing surfaces outside `(dashboard)` that are excluded from the
// marketing allowlist even though most of them do not share a
// REPLAY_BLOCKED_PATH_PREFIXES entry. `/sign-out` is a transient redirect
// page, not a marketing page, so it stays out too.
const OTHER_EXCLUDED_PREFIXES = [
  "/staff",
  "/sign-out",
  "/sumsub",
  "/return",
  "/fingerprint-test",
  "/dev",
  "/kyc",
  "/kyb",
] as const;

// The auth allowlist recording is started on.
const AUTH_ALLOWLIST_PREFIXES = [
  "/sign-up",
  "/login",
  "/2fa",
  "/verify-email",
  "/forgot-password",
  "/reset-password",
  "/set-password",
] as const;

// `/buy` must match `/buy` and `/buy/step-2`, never `/buyback`.
function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function matchesAny(
  pathname: string,
  prefixes: readonly string[],
): boolean {
  return prefixes.some((prefix) => matchesPrefix(pathname, prefix));
}

export function isBlockedPath(pathname: string): boolean {
  return matchesAny(pathname, REPLAY_BLOCKED_PATH_PREFIXES);
}

function isDashboardPath(pathname: string): boolean {
  return matchesAny(
    pathname,
    DASHBOARD_SEGMENTS.map((segment) => `/${segment}`),
  );
}

/**
 * Recording starts only on the auth allowlist or on marketing
 * pages. Marketing excludes every REPLAY_BLOCKED_PATH_PREFIXES entry, every
 * `(dashboard)` segment and the other non-marketing surfaces above.
 */
export function replayAllowed(pathname: string): boolean {
  if (matchesAny(pathname, AUTH_ALLOWLIST_PREFIXES)) return true;
  if (isBlockedPath(pathname)) return false;
  if (isDashboardPath(pathname)) return false;
  if (matchesAny(pathname, OTHER_EXCLUDED_PREFIXES)) return false;
  return true;
}

// The default capture options for an unblocked surface, matching
// `init-options.ts`'s `buildInitOptions()` literally.
const DEFAULT_SURFACE_CONFIG: Partial<PostHogConfig> = {
  autocapture: true,
  capture_heatmaps: true,
  capture_dead_clicks: true,
};

// On a blocked path, only explicit `capture()` events are sent.
const BLOCKED_SURFACE_CONFIG: Partial<PostHogConfig> = {
  autocapture: false,
  capture_heatmaps: false,
  capture_dead_clicks: false,
};

/**
 * The `posthog.set_config(...)` partial for the surface at
 * `pathname`, applied on load and on client navigation, and restored when
 * leaving a blocked path (calling this again for the new pathname produces
 * the restored value).
 */
export function surfaceConfig(pathname: string): Partial<PostHogConfig> {
  return isBlockedPath(pathname)
    ? BLOCKED_SURFACE_CONFIG
    : DEFAULT_SURFACE_CONFIG;
}

/**
 * A replay `$snapshot` or a `$dead_click` must never reach PostHog for a
 * blocked path, on initial load or on a client navigation onto it. The
 * dead-click extension loads asynchronously and can start after
 * `capture_dead_clicks` was switched off, and a click queued before a
 * navigation can flush after it, so the event itself is dropped as well.
 * Every other event (`$pageview`, a custom capture) is unaffected.
 */
export function shouldDropEvent(eventName: string, pathname: string): boolean {
  return (eventName === "$snapshot" || eventName === "$dead_click") && isBlockedPath(pathname);
}

// Session replay's network capture (rrweb/network@1) records every request
// the page makes, including the ones third-party trackers fire themselves.
// Those requests put the page URL and the full referrer inside their own
// request URL (an analytics pixel's `dl`/`dr` query params, a tracking gif's
// query string), so a page visited with a sensitive query would leak it into
// replay even though `before_send` never sees that request. Keeping only
// first-party entries, and redacting their `name` the same way an event's
// URL properties are redacted, closes that gap without touching the events
// pipeline.

/**
 * posthog-js's `maskCapturedNetworkRequestFn`: returning `null` makes it drop
 * the entry entirely. `request.name` is the request's URL, absolute or
 * relative to the page; it is resolved against `pageOrigin` only to compare
 * origins, the value written back to `name` is `redactUrl` applied to the
 * original (unresolved) string, matching how event properties are redacted.
 */
export function firstPartyNetworkRequest(
  request: CapturedNetworkRequest,
  pageOrigin: string,
): CapturedNetworkRequest | null {
  const name = request.name;
  if (typeof name !== "string" || name.length === 0) return null;

  let url: URL;
  try {
    url = new URL(name, pageOrigin);
  } catch {
    return null;
  }
  if (url.origin !== pageOrigin) return null;

  return { ...request, name: redactUrl(name) };
}
